import { useEffect, useRef, useState } from 'react';
import { fetchFromDentalAPI } from './common';
import './LiveTaskAnalytics.css';

const FLASK_BASE_URL = 'https://newtonflaskserver-hfgkcrewf4fgedc6.westus-01.azurewebsites.net';

// Product fields may be a legacy boolean or a status string ("Onboarding"/"Live").
// Treat either form as "enabled".
const isProductEnabled = (v) =>
  v === true || (typeof v === 'string' && ['onboarding', 'live'].includes(v.trim().toLowerCase()));

const PERIOD_OPTIONS = [
  { label: 'Last 7 days', value: 7 },
  { label: 'Last 14 days', value: 14 },
  { label: 'Last 30 days', value: 30 },
  { label: 'Last 90 days', value: 90 },
];

function formatHours(hours) {
  if (!hours || hours === 0) return '—';
  if (hours < 1) return `${Math.round(hours * 60)}m`;
  if (hours < 24) return `${hours.toFixed(1)}h`;
  const d = Math.floor(hours / 24);
  const h = Math.round(hours % 24);
  return h > 0 ? `${d}d ${h}h` : `${d}d`;
}

function buildOrgData(practices, practiceStats, outboundStats = {}, inboundStats = {}) {
  const statsMap = {};
  for (const p of practiceStats) {
    statsMap[p.practiceId] = p;
  }

  const orgsMap = {};
  for (const practice of practices) {
    const orgId = practice.organizationId || practice.practiceId;
    if (!orgsMap[orgId]) {
      orgsMap[orgId] = {
        organizationId: orgId,
        organizationName: practice.organizationName || orgId,
        practices: [],
        totalTasks: 0,
        totalResolved: 0,
        firstActionTimeSum: 0,
        firstActionCount: 0,
        totalOutboundOutreach: 0,
        totalOutboundBookings: 0,
        totalInboundBookings: 0,
        productsSet: new Set(),
      };
    }
    const stats = statsMap[practice.practiceId] || { totalTasks: 0, totalResolved: 0, avgTimeToFirstAction: 0 };
    const ob = outboundStats[practice.practiceId] || { outreach: 0, bookings: 0 };
    const ib = inboundStats[practice.practiceId] || { bookings: 0 };
    const meta = practice.practiceMetadata || {};
    const loc = {
      practiceId: practice.practiceId,
      name: practice.name || practice.practiceId,
      outboundOutreach: ob.outreach,
      outboundBookings: ob.bookings,
      inboundBookings: ib.bookings,
      products: [isProductEnabled(meta.inbound) && 'Inbound', isProductEnabled(meta.outbound) && 'Outbound', isProductEnabled(meta.voip) && 'VoIP'].filter(Boolean),
      ...stats,
    };
    orgsMap[orgId].practices.push(loc);
    orgsMap[orgId].totalTasks += stats.totalTasks;
    orgsMap[orgId].totalResolved += stats.totalResolved;
    orgsMap[orgId].totalOutboundOutreach += ob.outreach;
    orgsMap[orgId].totalOutboundBookings += ob.bookings;
    orgsMap[orgId].totalInboundBookings += ib.bookings;
    loc.products.forEach(p => orgsMap[orgId].productsSet.add(p));
    if (stats.avgTimeToFirstAction > 0 && stats.firstActionCount > 0) {
      orgsMap[orgId].firstActionTimeSum += stats.avgTimeToFirstAction * stats.firstActionCount;
      orgsMap[orgId].firstActionCount += stats.firstActionCount;
    }
  }

  return Object.values(orgsMap).map(org => {
    return {
      ...org,
      products: Array.from(org.productsSet),
      displayName: org.organizationName,
      avgTimeToFirstAction: org.firstActionCount > 0
        ? Math.round((org.firstActionTimeSum / org.firstActionCount) * 100) / 100
        : 0,
      resolutionPct: org.totalTasks > 0
        ? Math.round((org.totalResolved / org.totalTasks) * 100)
        : 0,
    };
  }).filter(org => org.totalTasks > 0);
}

export default function LiveTaskAnalytics() {
  const [days, setDays] = useState(30);
  const [practices, setPractices] = useState([]);
  const [taskData, setTaskData] = useState(null);
  const [outboundStats, setOutboundStats] = useState({});
  const [inboundStats, setInboundStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedOrg, setExpandedOrg] = useState(null);
  const [sortField, setSortField] = useState('totalTasks');
  const [sortDir, setSortDir] = useState('desc');
  const practicesRequestSeq = useRef(0);
  const taskRequestSeq = useRef(0);
  const taskAbortController = useRef(null);

  // Fetch live practices from the Practices container
  useEffect(() => {
    const controller = new AbortController();
    const requestSeq = ++practicesRequestSeq.current;
    setLoading(true);
    fetchFromDentalAPI('/api/admin/live-practices', {
      signal: controller.signal,
    })
      .then(result => {
        if (requestSeq !== practicesRequestSeq.current) return;
        console.log('[LTA] Sample practice:', JSON.stringify(result.practices?.[0], null, 2));
        setPractices(result.practices || []);
      })
      .catch(err => {
        if (controller.signal.aborted || requestSeq !== practicesRequestSeq.current) return;
        console.error('Failed to fetch live practices:', err);
        setError(err.message);
      })
      .finally(() => {
        if (requestSeq === practicesRequestSeq.current) setLoading(false);
      });

    return () => controller.abort();
  }, []);

  // Fetch all range-dependent KPI data when practices load or the period changes.
  useEffect(() => {
    if (practices.length === 0) return;

    const practiceIds = practices.map(p => p.practiceId);
    taskAbortController.current?.abort();
    const controller = new AbortController();
    taskAbortController.current = controller;
    const requestSeq = ++taskRequestSeq.current;
    setLoading(true);
    setError(null);
    setTaskData(null);
    setOutboundStats({});
    setInboundStats({});

    const taskRequest = fetchFromDentalAPI('/api/admin/live-task-summary', {
      method: 'POST',
      body: JSON.stringify({ practiceIds, days }),
      signal: controller.signal,
    });

    const now = new Date();
    const rangeStart = new Date(now.getTime() - days * 24 * 60 * 60 * 1000);
    const startDateStr = rangeStart.toISOString();
    const endDateStr = now.toISOString();
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Chicago';
    const token = localStorage.getItem('jwtToken');

    const outboundRequest = Promise.allSettled(
      practices.map(async (p) => {
        const endpoint = `${FLASK_BASE_URL}/api/${p.practiceId}/recall-daily-stats?startDate=${startDateStr}&endDate=${endDateStr}&timezone=${timezone}`;
        const res = await fetch(endpoint, {
          signal: controller.signal,
          headers: {
            'ngrok-skip-browser-warning': 'true',
            ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
          },
        });
        if (!res.ok) return { practiceId: p.practiceId, data: null };
        const json = await res.json();
        return { practiceId: p.practiceId, data: json };
      })
    ).then(results => {
      const statsMap = {};
      for (const r of results) {
        if (r.status === 'fulfilled' && r.value?.data) {
          const { practiceId: pid, data: d } = r.value;
          statsMap[pid] = {
            outreach: d.outreach?.total ?? 0,
            bookings: (d.outreach?.bookingsMade ?? 0) + (d.outreach?.staffAssistedBookings ?? 0),
          };
        }
      }
      return statsMap;
    });

    const inboundRequest = fetchFromDentalAPI('/api/admin/inbound-bookings-summary', {
      method: 'POST',
      body: JSON.stringify({ practiceIds, days }),
      signal: controller.signal,
    }).then(result => {
      const statsMap = {};
      for (const p of (result.practices || [])) {
        statsMap[p.practiceId] = { bookings: p.totalBookings || 0 };
      }
      return statsMap;
    });

    Promise.all([taskRequest, outboundRequest, inboundRequest])
      .then(([taskResult, outboundResult, inboundResult]) => {
        if (requestSeq !== taskRequestSeq.current) return;
        setTaskData(taskResult);
        setOutboundStats(outboundResult);
        setInboundStats(inboundResult);
      })
      .catch(err => {
        if (controller.signal.aborted || requestSeq !== taskRequestSeq.current) return;
        console.error('Failed to fetch KPI data:', err);
        setError(err.message);
      })
      .finally(() => {
        if (!controller.signal.aborted && requestSeq === taskRequestSeq.current) {
          setLoading(false);
        }
      });
  }, [days, practices]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir(field === 'displayName' ? 'asc' : 'desc');
    }
  };

  const sortIndicator = (field) => {
    if (sortField !== field) return '';
    return sortDir === 'asc' ? ' ▲' : ' ▼';
  };

  const orgs = buildOrgData(practices, taskData?.practices || [], outboundStats, inboundStats);

  const sorted = [...orgs].sort((a, b) => {
    let av = a[sortField], bv = b[sortField];
    if (typeof av === 'string') { av = av.toLowerCase(); bv = (bv || '').toLowerCase(); }
    if (av < bv) return sortDir === 'asc' ? -1 : 1;
    if (av > bv) return sortDir === 'asc' ? 1 : -1;
    return 0;
  });

  const totals = orgs.reduce(
    (acc, o) => {
      acc.tasks += o.totalTasks;
      acc.resolved += o.totalResolved;
      acc.firstActionTimeSum += o.firstActionTimeSum;
      acc.firstActionCount += o.firstActionCount;
      acc.outboundOutreach += o.totalOutboundOutreach;
      acc.outboundBookings += o.totalOutboundBookings;
      acc.inboundBookings += o.totalInboundBookings;
      return acc;
    },
    { tasks: 0, resolved: 0, firstActionTimeSum: 0, firstActionCount: 0, outboundOutreach: 0, outboundBookings: 0, inboundBookings: 0 }
  );

  const avgFirstAction = totals.firstActionCount > 0
    ? Math.round((totals.firstActionTimeSum / totals.firstActionCount) * 100) / 100
    : 0;

  const handleRowClick = (orgId) => {
    setExpandedOrg(expandedOrg === orgId ? null : orgId);
  };

  return (
    <div className="lta-container">
      <div className="lta-header">
        <div>
          <h1 className="lta-title">Live Organizations</h1>
          <span className="lta-subtitle">
            {orgs.length} org{orgs.length !== 1 ? 's' : ''} · {practices.length} location{practices.length !== 1 ? 's' : ''}
          </span>
        </div>
        <select
          className="lta-period-select"
          value={days}
          onChange={e => setDays(Number(e.target.value))}
        >
          {PERIOD_OPTIONS.map(opt => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Summary cards */}
      <div className="lta-summary">
        <div className="lta-stat-card">
          <div className="lta-stat-label">Total Tasks</div>
          <div className="lta-stat-value">{loading ? '…' : totals.tasks.toLocaleString()}</div>
        </div>
        <div className="lta-stat-card">
          <div className="lta-stat-label">Total Resolved</div>
          <div className="lta-stat-value lta-stat-green">{loading ? '…' : totals.resolved.toLocaleString()}</div>
          {!loading && totals.tasks > 0 && (
            <div className="lta-stat-sub">{Math.round((totals.resolved / totals.tasks) * 100)}% resolution rate</div>
          )}
        </div>
        <div className="lta-stat-card">
          <div className="lta-stat-label">Avg Time to First Action</div>
          <div className="lta-stat-value">{loading ? '…' : formatHours(avgFirstAction)}</div>
        </div>
        <div className="lta-stat-card">
          <div className="lta-stat-label">Outbound Outreach ({days}d)</div>
          <div className="lta-stat-value">{loading ? '…' : totals.outboundOutreach.toLocaleString()}</div>
        </div>
        <div className="lta-stat-card">
          <div className="lta-stat-label">Outbound Bookings ({days}d)</div>
          <div className="lta-stat-value lta-stat-green">{loading ? '…' : totals.outboundBookings.toLocaleString()}</div>
          {!loading && totals.outboundOutreach > 0 && (
            <div className="lta-stat-sub">{Math.round((totals.outboundBookings / totals.outboundOutreach) * 100)}% booking rate</div>
          )}
        </div>
        <div className="lta-stat-card">
          <div className="lta-stat-label">Inbound Bookings ({days}d)</div>
          <div className="lta-stat-value lta-stat-green">{loading ? '…' : totals.inboundBookings.toLocaleString()}</div>
        </div>
      </div>

      {error && <div className="lta-error">Error: {error}</div>}

      {loading && !taskData && (
        <div className="lta-loading">Loading task data…</div>
      )}

      {/* Orgs table */}
      {taskData && (
        <div className="lta-card">
          <table className="lta-table">
            <thead>
              <tr>
                <th className="sortable" onClick={() => handleSort('displayName')}>
                  Organization{sortIndicator('displayName')}
                </th>
                <th>Products</th>
                <th className="right">Locations</th>
                <th className="right sortable" onClick={() => handleSort('totalTasks')}>
                  Tasks{sortIndicator('totalTasks')}
                </th>
                <th className="right sortable" onClick={() => handleSort('totalResolved')}>
                  Resolved{sortIndicator('totalResolved')}
                </th>
                <th className="right sortable" onClick={() => handleSort('resolutionPct')}>
                  Resolution %{sortIndicator('resolutionPct')}
                </th>
                <th className="right sortable" onClick={() => handleSort('avgTimeToFirstAction')}>
                  Avg First Action{sortIndicator('avgTimeToFirstAction')}
                </th>
                <th className="right sortable" onClick={() => handleSort('totalOutboundOutreach')}>
                  Outbound Outreach{sortIndicator('totalOutboundOutreach')}
                </th>
                <th className="right sortable" onClick={() => handleSort('totalOutboundBookings')}>
                  Outbound Bookings{sortIndicator('totalOutboundBookings')}
                </th>
                <th className="right sortable" onClick={() => handleSort('totalInboundBookings')}>
                  Inbound Bookings{sortIndicator('totalInboundBookings')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sorted.map(org => (
                <OrgRow
                  key={org.organizationId}
                  org={org}
                  isExpanded={expandedOrg === org.organizationId}
                  onClick={() => handleRowClick(org.organizationId)}
                />
              ))}
              {sorted.length === 0 && (
                <tr><td colSpan="10" className="lta-empty">No live organizations found</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function ResolutionBadge({ pct, tasks }) {
  if (tasks === 0) return <span className="lta-muted">—</span>;
  const cls = pct >= 80 ? 'lta-good' : pct >= 50 ? 'lta-warn' : 'lta-bad';
  return <span className={cls}>{pct}%</span>;
}

const PRODUCT_COLORS = {
  Inbound: { bg: '#ecfdf5', color: '#059669' },
  Outbound: { bg: '#eff6ff', color: '#2563eb' },
  VoIP: { bg: '#faf5ff', color: '#7c3aed' },
};

function ProductBadges({ products }) {
  if (!products || products.length === 0) return <span className="lta-muted">—</span>;
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {products.map(p => (
        <span key={p} style={{
          display: 'inline-block',
          padding: '2px 7px',
          borderRadius: 4,
          fontSize: 11,
          fontWeight: 600,
          background: PRODUCT_COLORS[p]?.bg || '#f1f5f9',
          color: PRODUCT_COLORS[p]?.color || '#475569',
        }}>{p}</span>
      ))}
    </div>
  );
}

function OrgRow({ org, isExpanded, onClick }) {
  return (
    <>
      <tr className={`lta-org-row ${isExpanded ? 'lta-org-expanded' : ''}`} onClick={onClick}>
        <td className="lta-org-name">
          <span className="lta-expand-icon">{isExpanded ? '▾' : '▸'}</span>
          {org.displayName}
        </td>
        <td><ProductBadges products={org.products} /></td>
        <td className="right mono">{org.practices.length}</td>
        <td className="right mono">{org.totalTasks}</td>
        <td className="right mono">{org.totalResolved}</td>
        <td className="right mono">
          <ResolutionBadge pct={org.resolutionPct} tasks={org.totalTasks} />
        </td>
        <td className="right mono">{formatHours(org.avgTimeToFirstAction)}</td>
        <td className="right mono">{org.totalOutboundOutreach}</td>
        <td className="right mono">{org.totalOutboundBookings}</td>
        <td className="right mono">{org.totalInboundBookings}</td>
      </tr>
      {isExpanded && org.practices.map(loc => (
        <tr key={loc.practiceId} className="lta-location-row">
          <td className="lta-location-name">{loc.name}</td>
          <td><ProductBadges products={loc.products} /></td>
          <td className="right mono lta-muted">{loc.practiceId}</td>
          <td className="right mono">{loc.totalTasks}</td>
          <td className="right mono">{loc.totalResolved}</td>
          <td className="right mono">
            <ResolutionBadge pct={loc.totalTasks > 0 ? Math.round((loc.totalResolved / loc.totalTasks) * 100) : 0} tasks={loc.totalTasks} />
          </td>
          <td className="right mono">{formatHours(loc.avgTimeToFirstAction)}</td>
          <td className="right mono">{loc.outboundOutreach}</td>
          <td className="right mono">{loc.outboundBookings}</td>
          <td className="right mono">{loc.inboundBookings}</td>
        </tr>
      ))}
    </>
  );
}
